/* eslint-disable camelcase */
import { connect } from 'react-redux';
import { createSelector } from 'reselect';
import FrameworkUtils from 'utils/framework';
import userUtils from 'utils/User';
import p13nUtils from 'utils/localStorage/P13n';
import BannerListBindings from 'analytics/bindingMethods/components/bccComponents/BannerList/BannerListBindings';
import { p13nSelector } from 'selectors/p13n/p13nSelector';
import { sendCmsComponentEvent, matchContexts } from 'analytics/utils/cmsComponents';
import { personalizationSelector } from 'viewModel/selectors/personalization/personalizationSelector';
import { coreUserDataSelector } from 'viewModel/selectors/user/coreUserDataSelector';
import constants from 'constants/content';
import Empty from 'constants/empty';
import anaConsts from 'analytics/constants';
import fetchTrendingContent from 'services/api/bannerList/fetchTrendingContent';
import PersonalizationUtils from 'utils/Personalization';
import { getUUID } from 'ai/utils/aiBeautyChat';

const {
    COMPONENT_TYPES: { BANNER_LIST }
} = constants;

const {
    CMS_COMPONENT_EVENTS: { IMPRESSION, ITEM_CLICK }
} = anaConsts;

const TRENDING_CONTENT = {
    homepage_marketingbanner: 'hpmktg',
    homepage_herocarousel: 'hphc'
};

const { wrapHOC } = FrameworkUtils;

const formatNBCTrackingData = (personalization, akamaiabtestdata) => {
    const uuid = getUUID() || 'UUID ';
    const {
        activeAbTestIds = [], abTestTraffic = '', abTestEndDate = '', isABTestEnabled = false, isDefault = false
    } = personalization || {};

    return `activeAbTestIds:${activeAbTestIds.join(',')};abTestTraffic:${abTestTraffic};abTestEndDate:${abTestEndDate};isABTestEnabled:${isABTestEnabled};isDefault:${isDefault};deviceIdentifier:${uuid};${akamaiabtestdata}`;
};

const functions = (dispatch, ownProps) => ({
    triggerImpression: function () {
        const { items } = ownProps;
        const personalizationData = p13nUtils.getAllPersonalizedCache();
        const biStatus = userUtils.getBiStatus();
        const profileId = userUtils.getProfileId();
        const profileStatus = userUtils.getProfileStatus();
        const bannersPersonalizedData = [];
        const userData = {
            biStatus,
            profileId,
            profileStatus
        };

        // Check for each item in the banner list to see if its context matches any of the
        // contexts for personalization data in the local storage.
        items.forEach(item => {
            const { personalization } = item;

            if (personalization) {
                const personalizationLocalData = matchContexts(personalization, personalizationData);

                if (personalizationLocalData) {
                    bannersPersonalizedData.push(personalizationLocalData);
                }
            }
        });

        // Only trigger analytics for viewable impression if they also have personalization data.
        if (bannersPersonalizedData.length > 0) {
            userData.bannersPersonalizedData = bannersPersonalizedData;
            BannerListBindings.bannerItemsImpression(userData);
        }
    },
    triggerCMSImpression: function (targets, renderItems, bannerListProps = {}) {
        const {
            items,
            sid,
            title = '',
            blPlacement = '',
            sys = {},
            personalization: defaultPersonalization = Empty.Object,
            akamaiabtestdata = ''
        } = bannerListProps || ownProps;

        const {
            isNBCEnabled = null,
            bannerPoolId = '',
            isTrendingEnabled = null,
            client_request_id = '',
            timestamp = '',
            context: parentContextId = '',
            guardrails = [],
            NBCSlotCounts = Empty.Object
        } = defaultPersonalization;

        const sourceItems = renderItems || items || [];

        const currentItems = sourceItems
            .map((item, index) => ({
                ...item,
                itemIndex: index,
                isNBCEnabled,
                blPlacement,
                isTrendingEnabled,
                parentContextId,
                timestamp: timestamp,
                clientRequestId: client_request_id,
                bannerPoolId,
                NBCSlotCounts: NBCSlotCounts?.web ?? null
            }))
            .filter((_, index) => targets.includes(index));

        const cmsEventData = {
            items: currentItems,
            eventName: IMPRESSION,
            title,
            sid,
            guardrails,
            publishedVersion: sys?.publishedVersion || '',
            component: BANNER_LIST,
            nbcTrackingData: formatNBCTrackingData(defaultPersonalization, akamaiabtestdata)
        };

        setTimeout(() => {
            sendCmsComponentEvent(cmsEventData);
        }, 1000);

        const handleSignInReload = () => sendCmsComponentEvent(cmsEventData);
        window.addEventListener(anaConsts.Event.SIGN_IN_RELOAD, handleSignInReload);
    },
    triggerClick: async function (data, position, bannerListProps = {}) {
        const { sid: bannerSid, variationData } = data;

        const {
            items,
            sid,
            title = '',
            blPlacement = '',
            personalization: defaultPersonalization = Empty.Object,
            akamaiabtestdata = ''
        } = bannerListProps || ownProps;

        const {
            isNBCEnabled = null,
            bannerPoolId = '',
            isTrendingEnabled = null,
            client_request_id = '',
            timestamp = '',
            context: parentContextId = '',
            guardrails = [],
            NBCSlotCounts = Empty.Object
        } = defaultPersonalization;

        const sourceItems = variationData ? [variationData] : items || [];

        const currentItems = sourceItems.map((item, index) => ({
            ...item,
            itemIndex: variationData ? position : index,
            isNBCEnabled,
            blPlacement,
            isTrendingEnabled,
            parentContextId,
            timestamp,
            clientRequestId: client_request_id,
            bannerPoolId,
            NBCSlotCounts: NBCSlotCounts?.web ?? null
        }));

        await sendCmsComponentEvent({
            items: currentItems,
            eventName: ITEM_CLICK,
            title,
            sid,
            guardrails,
            clickedSid: bannerSid,
            component: BANNER_LIST,
            nbcTrackingData: formatNBCTrackingData(defaultPersonalization, akamaiabtestdata)
        });
    },
    getTrendingContent: async function () {
        const { blPlacement } = ownProps;
        const uuid = getUUID() || 'UUID ';
        const placement = TRENDING_CONTENT[blPlacement];

        return fetchTrendingContent(placement, uuid).then(data => data);
    }
});

const connectedBannerList = connect(
    createSelector(
        coreUserDataSelector,
        p13nSelector,
        personalizationSelector,
        (_state, ownProps) => ownProps,
        (user, p13n, personalization, ownProps) => {
            const { isNBCEnabled = false, NBCSlotCounts = Empty.Object, bannerPoolId } = ownProps?.personalization || Empty.Object;
            const { isTrending } = ownProps;
            const { isPersonalizationInitializing } = personalization;
            const personalizedComponent = (isNBCEnabled && personalization.personalizedComponent) || Empty.Object;

            let isTrendingContentEnabled = false;

            if (isTrending || ownProps?.personalization?.isTrendingEnabled) {
                if (user.isAnonymous) {
                    isTrendingContentEnabled = true;
                } else {
                    if (isNBCEnabled && (Object.keys(personalizedComponent).length === 0 || personalizedComponent?.length === 0)) {
                        isTrendingContentEnabled = true;
                    }
                }
            }

            const showPersonalizationOverlay = PersonalizationUtils.shouldShowPersonalizationOverlay(isNBCEnabled);
            const nonTargettedVariations = showPersonalizationOverlay ? ownProps.items : undefined;
            const _items = [...ownProps.items];

            let itemsToShow = ownProps.items;

            // Limit the number of items to display given the slots count for signed in users if enabled for NBC
            if (!user.isAnonymous && isNBCEnabled && NBCSlotCounts?.web) {
                itemsToShow = _items.slice(0, NBCSlotCounts.web);
            }

            return {
                p13n,
                personalizedComponent,
                bannerPoolId: isNBCEnabled ? bannerPoolId : null,
                isPersonalizationInitializing,
                nonTargettedVariations,
                items: itemsToShow,
                isTrendingContentEnabled
            };
        }
    ),
    functions
);

const withBannerListProps = wrapHOC(connectedBannerList);

export {
    functions, withBannerListProps
};
